// TelcoTrack — lightbox zoom (kolečko, +/−, pinch, pan, reset) + view-only rotate.
// Bez nové závislosti. Orientace se nepersistuje (API/EXIF rotation není ve vieweru).

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  panZoomTransform,
  pointerDistance,
  pointerMidpoint,
  resetZoomTransform,
  rotatePhotoDeg,
  scaleFromWheelDelta,
  zoomTransformAtPoint,
  ZOOMABLE_PHOTO_DOUBLE_CLICK_SCALE,
  ZOOMABLE_PHOTO_MAX_SCALE,
  ZOOMABLE_PHOTO_MIN_SCALE,
  ZOOMABLE_PHOTO_STEP,
  type ZoomablePhotoTransform,
} from '../../../utils/zoomable-photo.util';

export interface ZoomablePhotoItem {
  url: string;
  uploadedBy?: string | null;
  /** Čas nahrání (ISO). Lightbox ho skládá do popisku „Nahrál:“. */
  uploadedAt?: string | null;
}

@Component({
  selector: 'app-zoomable-photo',
  templateUrl: './zoomable-photo.component.html',
  styleUrls: ['./zoomable-photo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZoomablePhotoComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() src = '';
  @Input() alt = 'Foto';
  /** Sourozenci v lightboxu — když je víc než 1, zobrazí se další/předchozí. */
  @Input() gallery: ZoomablePhotoItem[] | null = null;
  @Output() itemChange = new EventEmitter<ZoomablePhotoItem>();

  @ViewChild('viewport', { static: true }) private viewportRef?: ElementRef<HTMLElement>;

  transform: ZoomablePhotoTransform = resetZoomTransform();
  /** View-only rotation for this lightbox session; kept while browsing the gallery. */
  rotationDeg = 0;

  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinchStartDist = 0;
  private pinchStartScale = 1;
  private panning = false;
  private lastPan: { x: number; y: number } | null = null;
  private wheelCleanup?: () => void;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['src'] && !changes['src'].firstChange) {
      this.resetTransform();
      // Keep rotationDeg while browsing siblings in the same lightbox session.
    }
  }

  ngAfterViewInit(): void {
    const el = this.viewportRef?.nativeElement;
    if (!el) return;
    const onWheel = (event: WheelEvent): void => this.onWheel(event);
    el.addEventListener('wheel', onWheel, { passive: false });
    this.wheelCleanup = () => el.removeEventListener('wheel', onWheel);
  }

  ngOnDestroy(): void {
    this.wheelCleanup?.();
    this.pointers.clear();
  }

  get isZoomed(): boolean {
    return this.transform.scale > ZOOMABLE_PHOTO_MIN_SCALE + 0.001;
  }

  get canZoomIn(): boolean {
    return this.transform.scale < ZOOMABLE_PHOTO_MAX_SCALE - 0.001;
  }

  get canZoomOut(): boolean {
    return this.isZoomed;
  }

  get transformCss(): string {
    const { x, y, scale } = this.transform;
    return `translate(${x}px, ${y}px) scale(${scale})`;
  }

  get rotationCss(): string {
    return `rotate(${this.rotationDeg}deg)`;
  }

  get cursorClass(): string {
    if (this.panning) return 'is-grabbing';
    return this.isZoomed ? 'is-grab' : 'is-zoom-in';
  }

  get currentIndex(): number {
    if (!this.gallery?.length) return -1;
    return this.gallery.findIndex((item) => item.url === this.src);
  }

  get canGoPrev(): boolean {
    return !!this.gallery && this.gallery.length > 1 && this.currentIndex > 0;
  }

  get canGoNext(): boolean {
    return !!this.gallery && this.gallery.length > 1 && this.currentIndex >= 0
      && this.currentIndex < this.gallery.length - 1;
  }

  onToolbarPointer(event: Event): void {
    event.stopPropagation();
  }

  zoomIn(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.zoomBy(ZOOMABLE_PHOTO_STEP);
  }

  zoomOut(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.zoomBy(-ZOOMABLE_PHOTO_STEP);
  }

  resetZoom(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.resetTransform();
  }

  rotateLeft(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.rotationDeg = rotatePhotoDeg(this.rotationDeg, -90);
    this.resetTransform();
    this.cdr.markForCheck();
  }

  rotateRight(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.rotationDeg = rotatePhotoDeg(this.rotationDeg, 90);
    this.resetTransform();
    this.cdr.markForCheck();
  }

  goPrev(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.emitNeighbor(-1);
  }

  goNext(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.emitNeighbor(1);
  }

  onDoubleClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isZoomed) {
      this.resetTransform();
      return;
    }
    const origin = this.viewportPoint(event.clientX, event.clientY);
    this.apply(zoomTransformAtPoint(this.transform, ZOOMABLE_PHOTO_DOUBLE_CLICK_SCALE, origin.x, origin.y));
  }

  onPointerDown(event: PointerEvent): void {
    event.stopPropagation();
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      this.pinchStartDist = pointerDistance(a, b);
      this.pinchStartScale = this.transform.scale;
      this.panning = false;
      this.lastPan = null;
      return;
    }
    if (this.isZoomed) {
      this.panning = true;
      this.lastPan = { x: event.clientX, y: event.clientY };
    }
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.pointers.has(event.pointerId)) return;
    event.stopPropagation();
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()];
      if (this.pinchStartDist < 8) return;
      const origin = this.viewportPointFromClient(pointerMidpoint(a, b));
      const nextScale = this.pinchStartScale * (pointerDistance(a, b) / this.pinchStartDist);
      this.apply(zoomTransformAtPoint(this.transform, nextScale, origin.x, origin.y));
      return;
    }
    if (!this.panning || !this.lastPan) return;
    event.preventDefault();
    const dx = event.clientX - this.lastPan.x;
    const dy = event.clientY - this.lastPan.y;
    this.lastPan = { x: event.clientX, y: event.clientY };
    this.apply(panZoomTransform(this.transform, dx, dy));
  }

  onPointerUp(event: PointerEvent): void {
    event.stopPropagation();
    this.pointers.delete(event.pointerId);
    if (this.pointers.size < 2) {
      this.pinchStartDist = 0;
    }
    if (this.pointers.size === 0) {
      this.panning = false;
      this.lastPan = null;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKey(event: KeyboardEvent): void {
    const key = event.key;
    if (key === '+' || key === '=') {
      event.preventDefault();
      this.zoomBy(ZOOMABLE_PHOTO_STEP);
      return;
    }
    if (key === '-' || key === '_') {
      event.preventDefault();
      this.zoomBy(-ZOOMABLE_PHOTO_STEP);
      return;
    }
    if (key === '0') {
      event.preventDefault();
      this.resetTransform();
      return;
    }
    if (key === 'ArrowLeft' && this.canGoPrev) {
      event.preventDefault();
      this.emitNeighbor(-1);
      return;
    }
    if (key === 'ArrowRight' && this.canGoNext) {
      event.preventDefault();
      this.emitNeighbor(1);
      return;
    }
    if ((key === 'l' || key === 'L' || key === '[') && !this.isTextEntryTarget(event)) {
      event.preventDefault();
      this.rotationDeg = rotatePhotoDeg(this.rotationDeg, -90);
      this.resetTransform();
      this.cdr.markForCheck();
      return;
    }
    if ((key === 'r' || key === 'R' || key === ']') && !this.isTextEntryTarget(event)) {
      event.preventDefault();
      this.rotationDeg = rotatePhotoDeg(this.rotationDeg, 90);
      this.resetTransform();
      this.cdr.markForCheck();
    }
  }

  /** Poznámka / input: R a L se musí vložit. Otáčení fotky platí jen mimo textové pole. */
  private isTextEntryTarget(event: KeyboardEvent): boolean {
    const raw = event.target;
    const node = raw instanceof Element ? raw : raw instanceof Node ? raw.parentElement : null;
    const field = node?.closest('textarea, input, [contenteditable]');
    if (!field) return false;
    if (field instanceof HTMLInputElement) {
      const type = (field.type || 'text').toLowerCase();
      if (
        type === 'button' ||
        type === 'checkbox' ||
        type === 'radio' ||
        type === 'submit' ||
        type === 'reset' ||
        type === 'file' ||
        type === 'range' ||
        type === 'color' ||
        type === 'image' ||
        type === 'hidden'
      ) {
        return false;
      }
    }
    if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLTextAreaElement)) {
      const editable = field.getAttribute('contenteditable');
      if (editable === 'false') return false;
    }
    return true;
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const origin = this.viewportPoint(event.clientX, event.clientY);
    const nextScale = scaleFromWheelDelta(this.transform.scale, event.deltaY);
    this.apply(zoomTransformAtPoint(this.transform, nextScale, origin.x, origin.y));
  }

  private zoomBy(delta: number): void {
    const el = this.viewportRef?.nativeElement;
    const origin = el
      ? { x: el.clientWidth / 2, y: el.clientHeight / 2 }
      : { x: 0, y: 0 };
    this.apply(zoomTransformAtPoint(this.transform, this.transform.scale + delta, origin.x, origin.y));
  }

  private emitNeighbor(step: number): void {
    const list = this.gallery;
    const index = this.currentIndex + step;
    if (!list || index < 0 || index >= list.length) return;
    this.itemChange.emit(list[index]);
  }

  private resetTransform(): void {
    this.apply(resetZoomTransform());
  }

  private apply(next: ZoomablePhotoTransform): void {
    this.transform = next;
    this.cdr.markForCheck();
  }

  private viewportPoint(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.viewportRef?.nativeElement.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  private viewportPointFromClient(client: { x: number; y: number }): { x: number; y: number } {
    return this.viewportPoint(client.x, client.y);
  }
}
